// Vendor sign-in, in two steps: email first, then — for a vendor with a 2FA
// factor — the code from that factor (docs/superpowers/specs/
// 2026-10-16-vendor-code-login-design.md §1.4).
//
// It is two steps because only the server knows whether an email belongs to
// an enrolled vendor. login/start answers with the method: an enrolled vendor
// gets the code step; everyone else (unknown, unenrolled, deactivated) gets
// the emailed link exactly as before. The link panel's copy is unchanged ON
// PURPOSE (E4): those three cases must stay indistinguishable from each other.
//
// E6: the SPA only ever calls setSession with the aal2 pair login/verify
// returns. No aal1 session reaches the browser in this flow; VendorMfaGate
// sees aal2 and renders the portal without a step-up.
//
// No SMS is auto-sent on mount anywhere here: the text goes out from
// login/start, on the vendor's own submit or Resend click. The multi-tab
// auto-send race fixed in PR #101 cannot recur on this screen.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CodeLoginStartResponse, CodeLoginVerifyResponse } from '@l2u/shared/codeLogin';
import { publicApi } from '@l2u/ui/lib/api.ts';
import { getSupabase } from '@l2u/ui/lib/supabase.ts';
import { setCompanyAccessToken } from '@l2u/ui/lib/auth/companyToken.ts';
import { errorText } from '@l2u/ui/lib/errorText.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, TextInput } from '@l2u/ui/components/Field.tsx';
import { CodeInput } from '@l2u/ui/auth/CodeInput.tsx';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { BRAND } from '@l2u/ui/lib/brand.ts';
import { AuthSplitLayout, AuthInfoChip } from '@l2u/ui/components/AuthSplitLayout.tsx';
import {
  cooldownRemaining,
  lockedMessage,
  smsPrompt,
  verifyFailure,
  viewAfterStart,
  viewAfterTick,
  type LoginView,
} from './codeLogin.ts';

// `initialView` is the test seam (no jsdom here, so tests cannot click their
// way to step 2); App.tsx renders this with no props.
export function CompanyLogin({ initialView }: { initialView?: LoginView } = {}) {
  const { session } = useCompanyAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<LoginView>(() => initialView ?? { step: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  // Step 1 keeps today's ErrorMessage banner for transport errors; step 2
  // shows a short line under the code field instead.
  const [startError, setStartError] = useState<unknown>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // A ref, not state: the auto-submit at six digits and the button can fire
  // in the same tick, before a state update would be visible to either.
  const inFlight = useRef(false);

  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  const cooldown = view.step === 'code' ? cooldownRemaining(view.cooldownUntil, now) : 0;
  const ticking = view.step === 'locked' || cooldown > 0;
  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      setView((v) => viewAfterTick(v, n));
    }, 1000);
    return () => clearInterval(t);
  }, [ticking]);

  function show(next: LoginView) {
    setNow(Date.now());
    setView(next);
  }

  async function startLogin(): Promise<void> {
    const res = await publicApi.post<CodeLoginStartResponse>('/api/auth/company/login/start', {
      email: email.trim(),
    });
    show(viewAfterStart(res, Date.now()));
  }

  async function onEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setStartError(null);
    try {
      await startLogin();
    } catch (err) {
      setStartError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(value: string) {
    if (value.length !== 6 || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setCodeError(null);
    try {
      const tokens = await publicApi.post<CodeLoginVerifyResponse>(
        '/api/auth/company/login/verify',
        { email: email.trim(), code: value },
      );
      const { error } = await getSupabase().auth.setSession(tokens);
      if (error) throw error;
      setCompanyAccessToken(tokens.access_token); // the session effect above navigates
    } catch (err) {
      const f = verifyFailure(err, Date.now());
      // Clear the rejected digits — the field is at maxLength, so otherwise
      // the vendor must backspace six times to try again.
      setCode('');
      if (f.kind === 'message') setCodeError(f.text);
      else if (f.kind === 'locked') show({ step: 'locked', until: f.until, reason: f.reason });
      else {
        // The vendor's factor changed since start (reset, or the email moved):
        // back to step 1 and ask again, silently, with the same email.
        show({ step: 'email' });
        try {
          await startLogin();
        } catch (startErr) {
          setStartError(startErr);
        }
      }
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  }

  function onCode(next: string) {
    setCode(next);
    if (next.length < 6) setCodeError(null);
    if (next.length === 6) void verify(next); // auto-verify; the button is the fallback
  }

  async function resend() {
    if (view.step !== 'code' || resending || cooldown > 0) return;
    setCodeError(null);
    setResending(true);
    try {
      await startLogin();
      setCode('');
    } catch (err) {
      setCodeError(errorText(err));
    } finally {
      setResending(false);
    }
  }

  // Empties the email too: the locked panel offers this for the next person
  // on a shared computer, who should not see the locked vendor's address.
  function backToEmail() {
    setView({ step: 'email' });
    setEmail('');
    setCode('');
    setCodeError(null);
    setStartError(null);
  }

  return (
    <AuthSplitLayout
      variant="vendor"
      formFooter={
        <>
          Need help?{' '}
          <a
            href={`mailto:${BRAND.supportEmail}`}
            className="font-semibold text-brand-primary hover:underline"
          >
            Email {BRAND.supportEmail}
          </a>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {view.step === 'code' ? (
          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl text-slate-900">Enter your code</h2>
            <p className="text-sm text-slate-500">
              {view.factor === 'sms'
                ? smsPrompt(view.phoneHint)
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl text-slate-900">Welcome back</h2>
            {view.step === 'email' && (
              <p className="text-sm text-slate-500">Enter your work email to continue.</p>
            )}
          </div>
        )}

        {view.step === 'sent' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            If that email matches an account, we’ve sent a sign-in link. It’s valid for 7 days —
            check your inbox to sign in.
          </div>
        )}

        {view.step === 'email' && (
          <form className="flex flex-col gap-5" onSubmit={onEmailSubmit}>
            <Field label="Work email" required>
              <TextInput
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                style={{ borderRadius: 10 }}
              />
            </Field>
            <ErrorMessage error={startError} />
            <Btn
              type="submit"
              loading={submitting}
              disabled={!email.trim()}
              className="w-full"
              style={{ backgroundColor: 'var(--brand-orange)', borderRadius: 10, height: 48 }}
            >
              Continue
            </Btn>
            <AuthInfoChip tone="purple">
              If you’ve set up two-factor sign-in you’ll enter your code next; otherwise we’ll email
              you a sign-in link.
            </AuthInfoChip>
          </form>
        )}

        {view.step === 'code' && (
          <div className="flex flex-col gap-4">
            <CodeInput value={code} onChange={onCode} disabled={submitting} autoFocus />
            {codeError && (
              // role="alert" so a screen reader announces a rejected code; the
              // auto-verify means there is no submit event to hear otherwise.
              <p role="alert" className="text-sm text-red-600">
                {codeError}
              </p>
            )}
            <Btn
              onClick={() => void verify(code)}
              loading={submitting}
              disabled={code.length < 6}
              className="w-full"
              style={{ backgroundColor: 'var(--brand-orange)', borderRadius: 10, height: 48 }}
            >
              Verify and continue
            </Btn>
            {view.factor === 'sms' && (
              <button
                type="button"
                onClick={() => void resend()}
                disabled={submitting || resending || cooldown > 0}
                className="self-start text-sm font-semibold text-brand-primary disabled:opacity-50"
              >
                {resending
                  ? 'Sending…'
                  : cooldown > 0
                    ? `Resend code in ${cooldown}s`
                    : 'Resend code'}
              </button>
            )}
            <button
              type="button"
              onClick={backToEmail}
              className="self-start text-sm font-semibold text-slate-500 hover:underline"
            >
              Use a different email
            </button>
          </div>
        )}

        {view.step === 'locked' && (
          // Deliberately no "email me a link instead" (E5). "Use a different
          // email" only goes back to step 1, where a locked vendor's own email
          // is refused again by login/start. The tick returns it to step 1
          // when the time is up.
          <div className="flex flex-col gap-4">
            <div
              role="status"
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800"
            >
              {lockedMessage(view.until, now, view.reason)}
            </div>
            <button
              type="button"
              onClick={backToEmail}
              className="self-start text-sm font-semibold text-slate-500 hover:underline"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </AuthSplitLayout>
  );
}
